import { DownOutlineIcon } from '@carboncopyco/icons';
import type { IGlobalTab } from '@carboncopyco/types/desktop';
import { ColorHex } from '@carboncopyco/ui';
import { useClickOutside } from '@carboncopyco/utils';
import type { ReactNode } from 'react';
import useMoreTabsDropdown from '../../../hooks/useMoreTabsDropdown';
import MenuTab from './items/MenuTab';
import type { DefaultThemeType } from './theme';

const MoreTabsDropdown = ({
  tabs,
  activeTabId,
  onTabClick,
  theme,
  dropdownIcon,
  openOnClick,
}: {
  tabs: IGlobalTab[];
  activeTabId: IGlobalTab['id'];
  onTabClick: (activeTabId: IGlobalTab['id']) => void;
  theme: DefaultThemeType;
  dropdownIcon?: ReactNode;
  openOnClick?: boolean;
}) => {
  const { isOpen, handleIsOpen } = useMoreTabsDropdown();

  const dropdownRef = useClickOutside(() => {
    if (isOpen) handleIsOpen(false);
  }, [isOpen]);

  const menuTriggerClasses =
    openOnClick && isOpen
      ? '!bg-[var(--ds-tab-inactive-hover-bg)]'
      : 'hover:!bg-[var(--ds-tab-inactive-hover-bg)]';

  const handleIsOpenOnClick = () => {
    handleIsOpen(!isOpen);
  };

  return (
    <div
      ref={openOnClick ? dropdownRef : undefined}
      className="flex justify-center flex-grow relative z-[201] border-s border-[--ds-tab-common-divider-color]"
      onMouseEnter={openOnClick ? undefined : () => handleIsOpen(true)}
      onMouseLeave={openOnClick ? undefined : () => handleIsOpen(false)}
      data-testid="global-tab-menu"
    >
      <button
        type="button"
        className={`flex items-center justify-center w-10 cursor-pointer flex-grow !bg-[var(--ds-tab-inactive-bg)] ${menuTriggerClasses}`}
        data-testid="global-tab-menu-trigger"
        onClick={openOnClick ? handleIsOpenOnClick : undefined}
      >
        {dropdownIcon || <DownOutlineIcon color={ColorHex.natural50} />}
      </button>
      {isOpen && (
        <div className="absolute top-full end-0 pt-[var(--ds-tab-sizing-dropdown-gap)] pe-0.5">
          <ul className="grid gap-1 bg-ds-accent3-100 rounded-md shadow-xl p-1 overflow-y-auto max-h-[calc(100vh-var(--ds-tab-sizing-height)-40px)] !bg-[var(--ds-tab-inactive-bg)]">
            {tabs.map((tab, index) => {
              const isHomeTab = index === 0;

              const isInvisibleIfGroupIsActive =
                tab.group && !tab.group.isVisibleIfGroupIsActive;

              if (isHomeTab || isInvisibleIfGroupIsActive) {
                return null;
              }

              return (
                <li key={tab.id}>
                  <MenuTab
                    tab={tab}
                    activeTabId={activeTabId}
                    theme={theme}
                    onClick={() => {
                      handleIsOpen(false);
                      onTabClick?.(tab.id);
                    }}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MoreTabsDropdown;
